import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ColumnConfig } from './types';
import {
  getStoredColumnWidths,
  setStoredColumnWidths,
  MIN_COLUMN_WIDTH,
  MAX_COLUMN_WIDTH,
} from './columnWidthStorage';

export { MIN_COLUMN_WIDTH, MAX_COLUMN_WIDTH } from './columnWidthStorage';

/** Fallback when a width-less column was never measured (matches the
 *  long-standing filler fallback in EditableTable). */
export const DEFAULT_COLUMN_WIDTH = 120;

const clampWidth = (value: number) =>
  Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, value));

export interface UseColumnWidthsParams {
  columns: ColumnConfig[];
  /** When set, user-resized widths persist to localStorage under
   *  `editable-table:${storageKey}:column-widths`. Omit for session-only. */
  storageKey?: string;
  /** The header `<tr>` element — used to measure width-less columns and to
   *  locate the `<col>` / `<th>` / grouped-sub nodes during a drag. */
  headerRowRef: React.RefObject<HTMLTableRowElement | null>;
  /** Whether the table currently renders content rows. A measurement taken
   *  while the body is empty (query still loading) only reflects header
   *  widths — it's kept provisional and re-taken once rows first render so
   *  width-less columns size to their content. One-way: a measurement taken
   *  WITH rows stays valid if the table later empties (filtering). Defaults
   *  to true (callers that don't track data keep the old single-measure
   *  behavior). */
  hasRows?: boolean;
}

export interface UseColumnWidthsResult {
  /** `columns` with width resolved: user override ?? explicit `col.width` ?? measured. */
  effectiveColumns: ColumnConfig[];
  /** True once every column has a resolved width — switches the table to
   *  `table-layout: fixed` driven by the `<colgroup>`. Stays false in
   *  environments without real layout (jsdom) when measurement is needed. */
  layoutReady: boolean;
  /** Resolved width for a column, or undefined while unmeasured. */
  getWidth: (colData: string) => number | undefined;
  /** Begin a drag gesture from a resize handle's pointerdown. */
  startResize: (colData: string, e: React.PointerEvent) => void;
  /** Commit a width programmatically (drag uses this on pointerup). */
  setWidth: (colData: string, width: number) => void;
  /** Drop the user override — column falls back to its configured or
   *  measured width (or the 120px default when neither exists). */
  resetWidth: (colData: string) => void;
}

interface DragState {
  colData: string;
  pointerId: number;
  startX: number;
  startWidth: number;
  latestX: number;
  raf: number;
  cleanup: () => void;
}

/**
 * Column-width state for `EditableTable`: user overrides (optionally
 * persisted per table type), a measure-then-fix pass that captures the
 * auto-layout widths of width-less columns before switching the table to
 * fixed layout, and the drag lifecycle for the header resize handles.
 *
 * Drag perf contract: pointermove never sets React state — it writes the
 * dragged column's `<col>`/`<th>`/grouped-sub widths directly via the DOM
 * (rAF-throttled). One state commit happens on pointerup. See the
 * `cellStyleByCol` perf note in EditableTable.tsx for why per-frame state
 * updates are off the table.
 */
export function useColumnWidths({
  columns,
  storageKey,
  headerRowRef,
  hasRows = true,
}: UseColumnWidthsParams): UseColumnWidthsResult {
  const [overrides, setOverrides] = useState<Record<string, number>>(() =>
    storageKey ? getStoredColumnWidths(storageKey) : {},
  );

  // Re-hydrate when storageKey changes on a live instance (no caller does
  // this today, but without it the persist effect below would write the OLD
  // table's widths under the NEW key). Render-phase state adjustment per
  // the React "adjusting state when a prop changes" pattern — synchronous,
  // so the swapped widths paint in the same render.
  const [hydratedKey, setHydratedKey] = useState(storageKey);
  if (hydratedKey !== storageKey) {
    setHydratedKey(storageKey);
    setOverrides(storageKey ? getStoredColumnWidths(storageKey) : {});
  }

  // Measured widths are tied to the column key + header signature so both a
  // different column set (e.g. data-driven month columns) AND a header label
  // change (i18n language switch — "Normal Balance" vs "Normaal saldo")
  // re-measure from scratch. While re-measuring, width-less columns drop the
  // table back to auto layout for a frame, so content-sized columns adapt to
  // the new labels instead of keeping widths measured under the old locale.
  const signature = useMemo(
    () => columns.map(c => `${c.data} ${c.header}`).join('|'),
    [columns],
  );
  const [measureResult, setMeasureResult] = useState<{
    sig: string;
    widths: Record<string, number>;
    /** Whether content rows were present at measure time. Empty-body
     *  measurements are provisional — discarded once rows first render. */
    withRows: boolean;
  } | null>(null);
  const measured =
    measureResult?.sig === signature && (measureResult.withRows || !hasRows)
      ? measureResult.widths
      : null;

  const effectiveColumns = useMemo(
    () =>
      columns.map(col => {
        const width = overrides[col.data] ?? col.width ?? measured?.[col.data];
        return width === col.width ? col : { ...col, width };
      }),
    [columns, overrides, measured],
  );

  const layoutReady = useMemo(
    () => effectiveColumns.every(c => typeof c.width === 'number' && c.width > 0),
    [effectiveColumns],
  );

  const widthByCol = useMemo(() => {
    const map = new Map<string, number | undefined>();
    for (const col of effectiveColumns) map.set(col.data, col.width);
    return map;
  }, [effectiveColumns]);

  const getWidth = useCallback((colData: string) => widthByCol.get(colData), [widthByCol]);

  // Refs so drag/reset callbacks stay referentially stable.
  const widthByColRef = useRef(widthByCol);
  const columnsRef = useRef(columns);
  const measuredRef = useRef(measured);
  useEffect(() => {
    widthByColRef.current = widthByCol;
    columnsRef.current = columns;
    measuredRef.current = measured;
  });

  // Measure-then-fix: capture the auto-layout rendered width of every
  // column that has neither an explicit width nor a stored override, so
  // switching to fixed layout doesn't move anything. Runs before paint.
  useLayoutEffect(() => {
    if (measured !== null) return;
    const unresolved = columns.filter(
      c => c.width === undefined && overrides[c.data] === undefined,
    );
    if (unresolved.length === 0) {
      // Nothing content-dependent to measure — mark withRows so the result
      // never gets invalidated by data arriving.
      setMeasureResult({ sig: signature, widths: {}, withRows: true });
      return;
    }
    const headerRow = headerRowRef.current;
    if (!headerRow) return;

    const tryMeasure = (): boolean => {
      const widths: Record<string, number> = {};
      for (const col of unresolved) {
        const esc = CSS.escape(col.data);
        // Plain columns render their own <th>; grouped columns share a
        // colSpan'd <th> and are measured via their sub-header div.
        const el = headerRow.querySelector<HTMLElement>(
          `th[data-col-key="${esc}"], .grouped-header-sub[data-col-key="${esc}"]`,
        );
        const width = el?.getBoundingClientRect().width ?? 0;
        // Zero width = not laid out (hidden tab, jsdom). Bail — the table
        // stays in auto layout, which renders exactly as before.
        if (!(width > 0)) return false;
        widths[col.data] = Math.round(width * 100) / 100;
      }
      setMeasureResult({ sig: signature, widths, withRows: hasRows });
      return true;
    };

    if (tryMeasure()) return;
    // Hidden at mount (display:none tab) — retry once layout happens.
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (tryMeasure()) ro.disconnect();
    });
    ro.observe(headerRow);
    return () => ro.disconnect();
  }, [measured, columns, overrides, signature, headerRowRef, hasRows]);

  // Persist overrides whenever they change after hydration.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!storageKey) return;
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      return;
    }
    setStoredColumnWidths(storageKey, overrides);
  }, [overrides, storageKey]);

  const setWidth = useCallback((colData: string, width: number) => {
    const rounded = clampWidth(Math.round(width));
    setOverrides(prev => (prev[colData] === rounded ? prev : { ...prev, [colData]: rounded }));
  }, []);

  const resetWidth = useCallback((colData: string) => {
    setOverrides(prev => {
      if (!(colData in prev)) return prev;
      const configWidth = columnsRef.current.find(c => c.data === colData)?.width;
      const fallback = configWidth ?? measuredRef.current?.[colData];
      if (fallback === undefined) {
        // Width-less column whose override was hydrated from storage —
        // it was never measured, so there's no baseline to return to.
        if (prev[colData] === DEFAULT_COLUMN_WIDTH) return prev;
        return { ...prev, [colData]: DEFAULT_COLUMN_WIDTH };
      }
      const next = { ...prev };
      delete next[colData];
      return next;
    });
  }, []);

  // Commit a drag. If the table was stretched to fill its container, freeze
  // every stretched column at the width it was rendered at, so the committed
  // layout matches what the user saw during the drag — otherwise the stretch
  // pass would re-scale everything from the unstretched widths on release.
  const commitResize = useCallback(
    (colData: string, width: number, renderedWidths: Map<string, number>) => {
      setOverrides(prev => {
        const next = { ...prev };
        for (const [key, rendered] of renderedWidths) {
          if (key === colData) continue;
          const resolved = widthByColRef.current.get(key);
          if (resolved !== undefined && Math.abs(rendered - resolved) >= 0.5) {
            next[key] = clampWidth(Math.round(rendered));
          }
        }
        next[colData] = clampWidth(Math.round(width));
        return next;
      });
    },
    [],
  );

  const dragRef = useRef<DragState | null>(null);
  // Abort a drag if the table unmounts mid-gesture.
  useEffect(() => () => dragRef.current?.cleanup(), []);

  const startResize = useCallback(
    (colData: string, e: React.PointerEvent) => {
      if (e.button !== 0 || dragRef.current) return;
      const headerRow = headerRowRef.current;
      const table = headerRow?.closest('table');
      const container = table?.closest<HTMLElement>('.editable-table-container') ?? null;
      const esc = CSS.escape(colData);
      // Direct DOM targets for the live drag. Under fixed layout the <col>
      // alone drives the column width; the <th> / grouped-sub keep the
      // header box in sync (and are the only effect pre-layoutReady).
      // Live-drag targets: EVERY matching <col>/<th>/grouped-sub inside the
      // container — the real table AND the floating-header clone, which mirrors
      // the same `data-col-key` colgroup. Mutating both keeps the pinned clone
      // resizing in lockstep with the drag. `realTh`/`realSub` (the real header
      // only) seed the start width.
      const colEls = container
        ? Array.from(container.querySelectorAll<HTMLTableColElement>(`col[data-col-key="${esc}"]`))
        : [];
      const thEls = container
        ? Array.from(container.querySelectorAll<HTMLElement>(`th[data-col-key="${esc}"]`))
        : [];
      const subEls = container
        ? Array.from(container.querySelectorAll<HTMLElement>(`.grouped-header-sub[data-col-key="${esc}"]`))
        : [];
      const realTh = headerRow?.querySelector<HTMLElement>(`th[data-col-key="${esc}"]`) ?? null;
      const realSub =
        headerRow?.querySelector<HTMLElement>(`.grouped-header-sub[data-col-key="${esc}"]`) ?? null;

      // Rendered widths from the real table's <colgroup>. The stretch pass in
      // EditableTable scales columns up to fill a wide container, so what the
      // user sees can be wider than the resolved (configured/stored) width.
      // The drag must start from the RENDERED width — starting from the
      // resolved one snapped the column narrower on the first frame (and on
      // a plain click, whose pointerup wrote it to the DOM).
      const renderedWidths = new Map<string, number>();
      for (const c of table?.querySelectorAll<HTMLTableColElement>('col[data-col-key]') ?? []) {
        const key = c.getAttribute('data-col-key');
        const px = parseFloat(c.style.width);
        if (key && px > 0) renderedWidths.set(key, px);
      }

      const startWidth =
        renderedWidths.get(colData) ??
        widthByColRef.current.get(colData) ??
        (realTh ?? realSub)?.getBoundingClientRect().width ??
        DEFAULT_COLUMN_WIDTH;

      e.preventDefault();
      const captured = e.currentTarget as Element;
      captured.setPointerCapture?.(e.pointerId);
      container?.classList.add('is-resizing');

      const state: DragState = {
        colData,
        pointerId: e.pointerId,
        startX: e.clientX,
        startWidth,
        latestX: e.clientX,
        raf: 0,
        cleanup: () => {},
      };

      const computeWidth = () =>
        clampWidth(Math.round(state.startWidth + (state.latestX - state.startX)));

      const applyWidth = (width: number) => {
        const px = `${width}px`;
        for (const c of colEls) c.style.width = px;
        for (const th of thEls) th.style.width = px;
        for (const s of subEls) s.style.width = px;
      };

      const onMove = (ev: PointerEvent) => {
        if (ev.pointerId !== state.pointerId) return;
        state.latestX = ev.clientX;
        if (state.raf) return;
        state.raf = requestAnimationFrame(() => {
          state.raf = 0;
          applyWidth(computeWidth());
        });
      };
      const onUp = (ev: PointerEvent) => {
        if (ev.pointerId !== state.pointerId) return;
        if (typeof ev.clientX === 'number') state.latestX = ev.clientX;
        state.cleanup();
        const finalWidth = computeWidth();
        if (finalWidth === Math.round(state.startWidth)) {
          // A click (or a drag back to where it started): restore the exact
          // rendered width and commit nothing — no override, no storage write.
          applyWidth(state.startWidth);
          return;
        }
        applyWidth(finalWidth);
        commitResize(colData, finalWidth, renderedWidths);
      };
      const onCancel = (ev: PointerEvent) => {
        if (ev.pointerId !== state.pointerId) return;
        state.cleanup();
        applyWidth(state.startWidth);
      };

      state.cleanup = () => {
        if (state.raf) cancelAnimationFrame(state.raf);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onCancel);
        container?.classList.remove('is-resizing');
        try {
          captured.releasePointerCapture?.(state.pointerId);
        } catch {
          // pointer already released – ignore
        }
        dragRef.current = null;
      };

      dragRef.current = state;
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onCancel);
    },
    [headerRowRef, commitResize],
  );

  return { effectiveColumns, layoutReady, getWidth, startResize, setWidth, resetWidth };
}
